import React, { useState, useEffect } from 'react'
import { toast } from 'react-toastify'
import { getProxies, SavedProxy, deleteProxy, updateProxy, saveProxy, validateProxyFormat } from '../../helpers/proxyStorage'
import Modal from '../Modal/Modal'
import { Header, Input } from '../ui'
import {
	ProxyList,
	ProxyItem,
	ProxyInfo,
	ProxyName,
	ProxyAddress,
	ProxyDate,
	ActionButtons,
	EditButton,
	DeleteButton,
	AddNewSection,
	AddButton,
	SaveButton,
	CancelButton,
	EmptyState,
	SearchInput
} from './styles'

interface ProxyManageModalProps {
	isOpen: boolean
	onClose: () => void
}

const ProxyManageModal: React.FC<ProxyManageModalProps> = ({
	isOpen,
	onClose
}) => {
	const [proxies, setProxies] = useState<SavedProxy[]>([])
	const [searchTerm, setSearchTerm] = useState('')
	const [editingId, setEditingId] = useState<string | null>(null)
	const [editName, setEditName] = useState('')
	const [editProxy, setEditProxy] = useState('')
	const [isAddingNew, setIsAddingNew] = useState(false)
	const [newName, setNewName] = useState('')
	const [newProxy, setNewProxy] = useState('')

	const loadProxies = () => {
		const savedProxies = getProxies()
		setProxies(savedProxies)
	}

	useEffect(() => {
		if (isOpen) {
			loadProxies()
		}
	}, [isOpen])

	const filteredProxies = proxies.filter(proxy =>
		proxy.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
		proxy.proxy.toLowerCase().includes(searchTerm.toLowerCase())
	)

	const handleEdit = (proxy: SavedProxy) => {
		setEditingId(proxy.id)
		setEditName(proxy.name)
		setEditProxy(proxy.proxy)
	}

	const handleSaveEdit = () => {
		if (!editingId) return

		if (!validateProxyFormat(editProxy)) {
			toast.error('Invalid proxy format. Use: login:pass@ip:port')
			return
		}

		const success = updateProxy(editingId, {
			name: editName.trim() || 'Unnamed Proxy',
			proxy: editProxy.trim()
		})

		if (success) {
			toast.success('Proxy updated successfully')
			setEditingId(null)
			setEditName('')
			setEditProxy('')
			loadProxies()
		} else {
			toast.error('Failed to update proxy')
		}
	}

	const handleCancelEdit = () => {
		setEditingId(null)
		setEditName('')
		setEditProxy('')
	}

	const handleDelete = (id: string, name: string) => {
		if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
			const success = deleteProxy(id)
			if (success) {
				toast.success('Proxy deleted successfully')
				loadProxies()
			} else {
				toast.error('Failed to delete proxy')
			}
		}
	}

	const handleAddNew = () => {
		if (!validateProxyFormat(newProxy)) {
			toast.error('Invalid proxy format. Use: login:pass@ip:port')
			return
		}

		const savedProxy = saveProxy(newProxy.trim(), newName.trim() || undefined)
		
		if (savedProxy) {
			toast.success(`Proxy "${savedProxy.name}" added successfully`)
			setIsAddingNew(false)
			setNewName('')
			setNewProxy('')
			loadProxies()
		} else {
			toast.error('Failed to add proxy')
		}
	}

	const handleCancelAdd = () => {
		setIsAddingNew(false)
		setNewName('')
		setNewProxy('')
	}

	if (!isOpen) return null

	return (
		<Modal close={onClose}>
			<Header>Manage Proxies</Header>
			
			<SearchInput
				id='manage-search'
				placeholder='Search proxies...'
				value={searchTerm}
				onChange={(e) => setSearchTerm(e.target.value)}
			/>

			<AddNewSection>
				{!isAddingNew ? (
					<AddButton type="button" onClick={() => setIsAddingNew(true)}>
						+ Add New Proxy
					</AddButton>
				) : (
					<div>
						<Input
							id='new-proxy-name'
							placeholder='Proxy name (optional)...'
							value={newName}
							onChange={(e) => setNewName(e.target.value)}
							style={{ marginBottom: '10px' }}
						/>
						<Input
							id='new-proxy-address'
							placeholder='login:pass@ip:port'
							value={newProxy}
							onChange={(e) => setNewProxy(e.target.value)}
							style={{ marginBottom: '10px' }}
						/>
						<div style={{ display: 'flex', gap: '8px', marginTop: '15px' }}>
							<SaveButton type="button" onClick={handleAddNew}>Add</SaveButton>
							<CancelButton type="button" onClick={handleCancelAdd}>Cancel</CancelButton>
						</div>
					</div>
				)}
			</AddNewSection>

			{filteredProxies.length === 0 ? (
				<EmptyState>
					{proxies.length === 0 
						? 'No saved proxies found. Add a new proxy above.'
						: 'No proxies match your search.'
					}
				</EmptyState>
			) : (
				<ProxyList>
					{filteredProxies.map((proxy) => (
						<ProxyItem key={proxy.id}>
							{editingId === proxy.id ? (
								<div style={{ flex: 1 }}>
									<Input
										id={`edit-name-${proxy.id}`}
										placeholder='Proxy name...'
										value={editName}
										onChange={(e) => setEditName(e.target.value)}
										style={{ marginBottom: '10px' }}
									/>
									<Input
										id={`edit-proxy-${proxy.id}`}
										placeholder='login:pass@ip:port'
										value={editProxy}
										onChange={(e) => setEditProxy(e.target.value)}
										style={{ marginBottom: '10px' }}
									/>
									<div style={{ display: 'flex', gap: '8px', marginTop: '15px' }}>
										<SaveButton type="button" onClick={handleSaveEdit}>Save</SaveButton>
										<CancelButton type="button" onClick={handleCancelEdit}>Cancel</CancelButton>
									</div>
								</div>
							) : (
								<>
									<ProxyInfo>
										<ProxyName>{proxy.name}</ProxyName>
										<ProxyAddress>{proxy.proxy}</ProxyAddress>
										<ProxyDate>Created: {new Date(proxy.createdAt).toLocaleDateString()}</ProxyDate>
									</ProxyInfo>
									<ActionButtons>
										<EditButton type="button" onClick={() => handleEdit(proxy)}>
											Edit
										</EditButton>
										<DeleteButton type="button" onClick={() => handleDelete(proxy.id, proxy.name)}>
											Delete
										</DeleteButton>
									</ActionButtons>
								</>
							)}
						</ProxyItem>
					))}
				</ProxyList>
			)}
		</Modal>
	)
}

export default ProxyManageModal